import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI, locationsAPI } from '../../../api/bookings';

export function usePendingRequests() {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [courtsAssigned, setCourtsAssigned] = useState([]);
  const [availableCourts, setAvailableCourts] = useState([]);
  const [assignedCourtNumbers, setAssignedCourtNumbers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  // Discount state
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountReason, setDiscountReason] = useState('');

  // Filters — seeded from the inbound URL (e.g. the Dashboard's "View all"
  // link passes ?date=...&location_id=...) via lazy useState initializers,
  // so the very first `loadPendingBookings()` call below already uses the
  // right values. A separate effect that set these AFTER mount used to race
  // against the initial-load effect: the fetch fired with the stale empty
  // filters before the URL-derived state had a chance to apply, so the
  // date boxes looked pre-filled but the list underneath was unfiltered.
  const [searchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date') || '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date') || '');
  const [locationFilter, setLocationFilter] = useState(() => searchParams.get('location_id') || 'all');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [bookingRefFilter, setBookingRefFilter] = useState(() => searchParams.get('ref') || '');
  const [emailFilter, setEmailFilter] = useState('');

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getAllLocations();
      setLocationsList(locs);
      const locsMap = {};
      locs.forEach((loc) => { locsMap[loc.id] = loc; });
      setLocations(locsMap);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  const loadPendingBookings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPendingBookings(dateFrom, dateTo);
      setBookings(data);
    } catch (error) {
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadLocations();
    loadPendingBookings();
    // Initial load only — subsequent reloads go through explicit handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters whenever bookings or local filters change
  useEffect(() => {
    let filtered = [...bookings];

    if (locationFilter && locationFilter !== 'all') {
      filtered = filtered.filter((b) => b.location_id === locationFilter);
    }
    if (phoneFilter.trim()) {
      const searchPhone = phoneFilter.trim().toLowerCase();
      filtered = filtered.filter((b) => {
        const guestPhone = b.guest_phone?.toLowerCase() || '';
        const userPhone = b.customer_info?.user_phone?.toLowerCase() || '';
        return guestPhone.includes(searchPhone) || userPhone.includes(searchPhone);
      });
    }
    if (bookingRefFilter.trim()) {
      const searchRef = bookingRefFilter.trim().toLowerCase().replace('#', '');
      filtered = filtered.filter((b) => {
        const bookingId = b.id?.toLowerCase() || '';
        const shortRef = bookingId.slice(-8);
        return shortRef.includes(searchRef) || bookingId.includes(searchRef);
      });
    }
    if (emailFilter.trim()) {
      const searchEmail = emailFilter.trim().toLowerCase();
      filtered = filtered.filter((b) => {
        const guestEmail = b.guest_email?.toLowerCase() || '';
        const userEmail = b.customer_info?.user_email?.toLowerCase() || '';
        return guestEmail.includes(searchEmail) || userEmail.includes(searchEmail);
      });
    }

    setFilteredBookings(filtered);
  }, [bookings, locationFilter, phoneFilter, bookingRefFilter, emailFilter]);

  const applyDateFilters = useCallback(() => {
    loadPendingBookings();
  }, [loadPendingBookings]);

  const clearFilters = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setLocationFilter('all');
    setPhoneFilter('');
    setBookingRefFilter('');
    setEmailFilter('');
    // Immediately reload without filters
    adminAPI.getPendingBookings('', '')
      .then((data) => setBookings(data))
      .catch(() => toast.error('Failed to load pending requests'));
  }, []);

  const handleAssignClick = useCallback(async (booking) => {
    setSelectedBooking(booking);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/api/admin/available-court-numbers/${booking.location_id}/${booking.booking_date}/${booking.time_slot}?duration=${booking.duration || 1}`
      );
      const data = await response.json();

      setAvailableCourts(data.available_courts || []);
      setAssignedCourtNumbers(data.assigned_courts || []);

      const initialCourts = booking.courts_requested.map((court, idx) => ({
        sport: court.sport,
        court_number: data.available_courts[idx] || (idx + 1),
      }));
      setCourtsAssigned(initialCourts);
    } catch (error) {
      console.error('Failed to fetch available courts:', error);
      toast.error('Failed to load available courts');
      const initialCourts = booking.courts_requested.map((court, idx) => ({
        sport: court.sport,
        court_number: idx + 1,
      }));
      setCourtsAssigned(initialCourts);
      setAvailableCourts([1, 2, 3, 4, 5]);
      setAssignedCourtNumbers([]);
    }

    setDiscountType('none');
    setDiscountValue(0);
    setDiscountReason('');
    setIsDialogOpen(true);
  }, []);

  const updateCourtNumber = useCallback((index, courtNumber) => {
    setCourtsAssigned((prev) => {
      const updated = [...prev];
      updated[index].court_number = parseInt(courtNumber);
      return updated;
    });
  }, []);

  const getAvailableCourtNumbersForDropdown = useCallback((currentIndex) => {
    const selectedByOthers = courtsAssigned
      .map((court, idx) => (idx !== currentIndex ? court.court_number : null))
      .filter((num) => num !== null);
    return availableCourts.filter((num) => !selectedByOthers.includes(num));
  }, [courtsAssigned, availableCourts]);

  const handleAssignCourts = useCallback(async () => {
    try {
      if (discountType && discountType !== 'none' && discountValue > 0) {
        const estimatedTotal = selectedBooking.estimated_total || 0;
        let finalAmount = estimatedTotal;

        if (discountType === 'percentage') {
          finalAmount = estimatedTotal - (estimatedTotal * (parseFloat(discountValue) / 100));
        } else if (discountType === 'fixed') {
          finalAmount = estimatedTotal - parseFloat(discountValue);
        }

        if (finalAmount <= 0) {
          toast.error(
            `Invalid discount: Final amount would be $${finalAmount.toFixed(2)}. Discount exceeds booking total of $${estimatedTotal.toFixed(2)}. Please reduce the discount.`,
            { duration: 8000 }
          );
          return;
        }
      }

      await adminAPI.assignCourts(
        selectedBooking.id,
        courtsAssigned,
        discountType || null,
        parseFloat(discountValue) || 0,
        discountReason
      );
      toast.success('Courts assigned successfully');
      setIsDialogOpen(false);
      loadPendingBookings();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Cannot assign the same court multiple times. Please select different court numbers.', { duration: 6000 });
      } else if (error.response?.status === 409) {
        toast.error(error.response?.data?.detail || 'Court(s) already assigned to another booking. Please select different court numbers.', { duration: 6000 });
      } else {
        toast.error('Failed to assign courts');
      }
    }
  }, [discountType, discountValue, discountReason, selectedBooking, courtsAssigned, loadPendingBookings]);

  const handleCancelBooking = useCallback(async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking request?')) return;
    setCancelling(bookingId);
    try {
      await adminAPI.cancelBooking(bookingId);
      toast.success('Booking cancelled successfully');
      loadPendingBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(null);
    }
  }, [loadPendingBookings]);

  return {
    // data
    bookings, filteredBookings, locations, locationsList, loading,
    // dialog state
    isDialogOpen, setIsDialogOpen, selectedBooking,
    courtsAssigned, availableCourts, assignedCourtNumbers,
    // discount
    discountType, setDiscountType,
    discountValue, setDiscountValue,
    discountReason, setDiscountReason,
    // filters
    dateFrom, setDateFrom, dateTo, setDateTo,
    locationFilter, setLocationFilter,
    phoneFilter, setPhoneFilter,
    bookingRefFilter, setBookingRefFilter,
    emailFilter, setEmailFilter,
    cancelling,
    // actions
    applyDateFilters, clearFilters,
    handleAssignClick, updateCourtNumber, getAvailableCourtNumbersForDropdown,
    handleAssignCourts, handleCancelBooking,
  };
}
